import {
  ChangeDetectionStrategy,
  Component,
  computed,
  signal,
  output,
  inject,
  OnInit,
  OnDestroy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, NavigationEnd } from '@angular/router';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { filter, Subject, takeUntil } from 'rxjs';
import { ButtonModule } from 'primeng/button';
import { RippleModule } from 'primeng/ripple';
import { TooltipModule } from 'primeng/tooltip';
import { NgIconComponent, provideIcons } from '@ng-icons/core';
import {
  heroShieldCheckSolid,
  heroChartBarSolid,
  heroUsersSolid,
  heroUserSolid,
  heroCubeSolid,
  heroShieldExclamationSolid,
  heroDocumentCheckSolid,
  heroCog8ToothSolid,
} from '@ng-icons/heroicons/solid';
import { heroChevronLeft, heroChevronRight } from '@ng-icons/heroicons/outline';

interface MenuItem {
  id: string;
  label: string;
  icon: string;
  route: string;
  active?: boolean;
}

@Component({
  selector: 'app-sidenav',
  imports: [
    CommonModule,
    ButtonModule,
    RippleModule,
    TooltipModule,
    NgIconComponent,
  ],
  templateUrl: './sidenav.html',
  styleUrl: './sidenav.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    provideIcons({
      heroShieldCheckSolid,
      heroChartBarSolid,
      heroUsersSolid,
      heroUserSolid,
      heroCubeSolid,
      heroShieldExclamationSolid,
      heroDocumentCheckSolid,
      heroCog8ToothSolid,
      heroChevronLeft,
      heroChevronRight,
    }),
  ],
})
export class Sidenav implements OnInit, OnDestroy {
  // Inject Router for navigation
  private router = inject(Router);

  // Inject BreakpointObserver for responsive behavior
  private breakpointObserver = inject(BreakpointObserver);

  // Destroy subject for subscription cleanup
  private destroy$ = new Subject<void>();

  // Signal for collapsed state
  isCollapsed = signal(false);

  // Output event when collapsed state changes
  collapsedChange = output<boolean>();

  // Menu items configuration
  menuItems = signal<MenuItem[]>([
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: 'heroChartBarSolid',
      route: '/dashboard',
    },
    { id: 'users', label: 'Users', icon: 'heroUsersSolid', route: '/users' },
    {
      id: 'products',
      label: 'Products',
      icon: 'heroCubeSolid',
      route: '/products',
    },
    {
      id: 'roles',
      label: 'Roles',
      icon: 'heroShieldExclamationSolid',
      route: '/roles',
    },
    {
      id: 'audit',
      label: 'Audit Logs',
      icon: 'heroDocumentCheckSolid',
      route: '/audit',
    },
    {
      id: 'settings',
      label: 'Settings',
      icon: 'heroCog8ToothSolid',
      route: '/settings',
    },
  ]);

  // Computed sidebar class
  sidenavClass = computed(() => ({
    sidenav: true,
    'sidenav--collapsed': this.isCollapsed(),
  }));

  ngOnInit(): void {
    // Listen to router events to update active menu item
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.destroy$),
      )
      .subscribe((event: NavigationEnd) => {
        this.updateActiveMenuItem(event.url);
      });

    // Set initial active state based on current route
    this.updateActiveMenuItem(this.router.url);

    // Listen to screen size changes
    this.breakpointObserver
      .observe('(max-width: 768px)')
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => {
        if (result.matches) {
          // Mobile: force collapsed
          this.isCollapsed.set(true);
          this.collapsedChange.emit(true);
        } else {
          // Desktop: default to expanded (not collapsed)
          this.isCollapsed.set(false);
          this.collapsedChange.emit(false);
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private updateActiveMenuItem(url: string): void {
    this.menuItems.update((items) =>
      items.map((item) => {
        // Handle root route redirect to dashboard
        let isActive = false;
        if (url === '/' && item.route === '/dashboard') {
          isActive = true;
        } else if (url === item.route) {
          isActive = true;
        } else if (url.startsWith(item.route) && item.route !== '/') {
          // Avoid matching root route for all items
          isActive = true;
        }

        return {
          ...item,
          active: isActive,
        };
      }),
    );
  }

  toggleSidenav(): void {
    this.isCollapsed.update((current) => {
      const newValue = !current;
      this.collapsedChange.emit(newValue);
      return newValue;
    });
  }

  collapseOnOverlayClick(): void {
    // Collapse sidenav when overlay is clicked (typically on mobile)
    this.isCollapsed.set(true);
    this.collapsedChange.emit(true);
  }

  selectMenuItem(itemId: string): void {
    // Find the selected menu item
    const selectedItem = this.menuItems().find((item) => item.id === itemId);

    // Navigate to the corresponding route
    // The active state will be updated automatically by the router event listener
    if (selectedItem?.route) {
      this.router.navigate([selectedItem.route]);
    }
  }
}
